import React from 'react';

/**
 * Text- oder Betreff-Feld für Mailvorlagen, in dem `{{platzhalter}}` farbig markiert sind.
 * Technik: ein Hintergrund-Layer mit identischer Schrift/Padding rendert den Text mit Marken,
 * darüber liegt das echte Eingabefeld mit transparenter Schrift. Bearbeiten, Auswahl, Copy &
 * Paste und Undo bleiben damit die des Browsers.
 */

const TOKEN_RE = /\{\{\s*([a-z_]+)\s*\}\}/gi;

/** Gleiche Box wie die übrigen Felder (px-3 py-2 text-sm, 1px Rahmen). */
const BOX = 'w-full px-3 py-2 text-sm leading-relaxed font-normal rounded-lg border';

interface Props {
  value: string;
  onChange: (value: string) => void;
  /** Bekannte Platzhalter mit Anzeigenamen; unbekannte werden rot markiert. */
  labels: Record<string, string>;
  multiline?: boolean;
  rows?: number;
  id?: string;
  'aria-label'?: string;
}

export const TemplateTextField = React.forwardRef<HTMLTextAreaElement | HTMLInputElement, Props>(
  ({ value, onChange, labels, multiline = false, rows = 10, id, 'aria-label': ariaLabel }, ref) => {
    const backdropRef = React.useRef<HTMLDivElement | null>(null);

    const syncScroll = (e: React.SyntheticEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const b = backdropRef.current;
      if (!b) return;
      b.scrollTop = e.currentTarget.scrollTop;
      b.scrollLeft = e.currentTarget.scrollLeft;
    };

    const parts = React.useMemo(() => {
      const out: React.ReactNode[] = [];
      let last = 0;
      for (const m of value.matchAll(TOKEN_RE)) {
        const start = m.index ?? 0;
        if (start > last) out.push(value.slice(last, start));
        const key = m[1].toLowerCase();
        const known = key in labels;
        out.push(
          <mark
            key={`${start}-${key}`}
            className={`rounded-[4px] ${
              known
                ? 'bg-pipedrive-blue/10 text-pipedrive-blue ring-1 ring-pipedrive-blue/20'
                : 'bg-red-500/10 text-red-600 ring-1 ring-red-500/30'
            }`}
            title={known ? labels[key] : 'Unbekannter Platzhalter'}
          >
            {m[0]}
          </mark>,
        );
        last = start + m[0].length;
      }
      if (last < value.length) out.push(value.slice(last));
      // Abschließender Zeilenumbruch braucht ein Zeichen, sonst fehlt dem Layer die letzte Zeile.
      if (multiline) out.push('​');
      return out;
    }, [value, labels, multiline]);

    const fieldClass = `${BOX} relative block bg-transparent text-transparent caret-[var(--color-app-text)] border-app-border outline-none focus:ring-1 focus:ring-pipedrive-blue selection:bg-pipedrive-blue/25 selection:text-transparent`;

    return (
      <div className="relative rounded-lg bg-app-surface">
        <div
          ref={backdropRef}
          aria-hidden
          className={`${BOX} pointer-events-none absolute inset-0 overflow-hidden border-transparent text-app-text ${
            multiline ? 'whitespace-pre-wrap break-words [scrollbar-gutter:stable]' : 'whitespace-pre'
          }`}
        >
          {parts}
        </div>
        {multiline ? (
          <textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            id={id}
            aria-label={ariaLabel}
            value={value}
            rows={rows}
            spellCheck
            onChange={(e) => onChange(e.target.value)}
            onScroll={syncScroll}
            className={`${fieldClass} resize-y [scrollbar-gutter:stable]`}
          />
        ) : (
          <input
            ref={ref as React.Ref<HTMLInputElement>}
            id={id}
            aria-label={ariaLabel}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onScroll={syncScroll}
            onSelect={syncScroll}
            className={fieldClass}
          />
        )}
      </div>
    );
  },
);
TemplateTextField.displayName = 'TemplateTextField';
